import { Button } from '@civilgrid/ui/components';
import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import type React from 'react';
import { useState } from 'react';
import { createMissiveEmailDraft } from '~/api/civilsearch';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { ContactMethod, type DataRequestV2 } from '~/common/types';
import ConnectMissiveModal from './ConnectMissiveModal';
import LinkButton from './LinkButton';
import { getMissiveToken, searchRelatedMissiveEmail } from './missive';

interface Props {
  dataRequest: DataRequestV2;
}

export default function MissiveEmailHelpers({ dataRequest }: Props) {
  const { preferredContactMethod } = dataRequest.agency;
  const [isConnectOpen, setIsConnectOpen] = useState(false);
  const [hasToken, setHasToken] = useState(() => !!getMissiveToken());

  const createDraftMutation = useMutation({
    mutationKey: ['create-missive-email-draft', { requestId: dataRequest.id }],
    mutationFn: async () => {
      const token = getMissiveToken();
      if (!token) throw new Error('Missing Missive token');
      return createMissiveEmailDraft(dataRequest.id, token);
    },
    onSuccess: ({ webUrl }) => {
      // Creates a draft (send: false) — this does not send the email.
      window.open(webUrl, '_blank', 'noopener,noreferrer');
    },
    onError: (error) => {
      const status = isAxiosError(error) ? error.response?.status : undefined;
      if (status === 424) {
        // Expired/invalid PAT — prompt the user to reconnect Missive.
        setHasToken(false);
        setIsConnectOpen(true);
      } else {
        window.alert('An error occurred while creating the Missive draft. Post in #csearch-eng-working-group');
      }
    },
  });

  const draftButton = hasToken ? (
    <Button
      variant="secondary"
      loading={createDraftMutation.isPending}
      disabled={createDraftMutation.isPending}
      onClick={() => createDraftMutation.mutate()}
      data-testid="missive-create-draft"
    >
      Create Draft
    </Button>
  ) : (
    <Button variant="secondary" onClick={() => setIsConnectOpen(true)} data-testid="missive-connect">
      Connect to Missive
    </Button>
  );

  const buttons = (
    <div className="flex flex-col space-y-2">
      <div className="flex space-x-2">
        {draftButton}
        <Button
          variant="secondary"
          onClick={() => {
            capturePosthogEvent(POSTHOG_EVENTS.civilsearchMissiveEmailSearched, { request_id: dataRequest.id });
            searchRelatedMissiveEmail(dataRequest.requestId);
          }}
          data-testid="missive-search-related"
        >
          Search Related Emails
        </Button>
      </div>
    </div>
  );

  const withModal = (content: React.ReactNode) => (
    <>
      {content}
      <ConnectMissiveModal open={isConnectOpen} onOpenChange={setIsConnectOpen} onConnected={() => setHasToken(true)} />
    </>
  );

  if (preferredContactMethod === ContactMethod.Email) {
    return withModal(buttons);
  } else if (preferredContactMethod === ContactMethod.PortalContactForm) {
    return withModal(
      <div className="flex items-center space-x-2">
        {buttons}
        <LinkButton href={dataRequest.agency.portalContactFormUrl} data-testid="contact-form-page-link">
          Open Contact Form Page
        </LinkButton>
      </div>,
    );
  } else if (preferredContactMethod === ContactMethod.PublicRecordsRequest) {
    return withModal(
      <div className="flex items-center space-x-2">
        {buttons}
        <LinkButton href={dataRequest.agency.prrUrl} data-testid="prr-page-link">
          Open PRR Page
        </LinkButton>
      </div>,
    );
  } else {
    return <div>Error: Preferred contact method not set</div>;
  }
}
